import { ArrowRight, BookOpen, Library, Pill } from 'lucide-react';
import { Link } from 'react-router';
import { Alert, Badge, ButtonLink, SectionTitle, ruledList, rowHover, textLink } from '@/components/ui';
import { useNotifications } from '@/features/inbox/api';
import { useMyAssignments } from '@/features/learn/api';
import { usePendingAcknowledgments } from '@/features/protocols/api';
import { ToolIndex } from '@/features/tools/ToolsPage';
import { TOOLS } from '@/features/tools/registry';
import { useAuth } from '@/lib/auth';
import { APP_NAME, isBackendConfigured } from '@/lib/env';
import { cn, formatDate, relativeTime } from '@/lib/utils';

const QUICK_TOOLS = ['peds', 'rsi', 'dosing', 'stroke', 'gcs', 'burns'];

function QuickTools() {
  return (
    <section>
      <SectionTitle action={<Link to="/tools" className={textLink}>All tools</Link>}>Point-of-care tools</SectionTitle>
      <ToolIndex tools={QUICK_TOOLS.map((id) => TOOLS.find((t) => t.id === id)!)} />
    </section>
  );
}

function SignedOutHome() {
  return (
    <div className="space-y-12">
      <section className="border-b border-stone-300 pb-8 pt-2 dark:border-stone-700">
        <p className="label-caps text-stone-500 dark:text-stone-400">Clinical reference for EMS</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">{APP_NAME}</h1>
        <p className="mt-3 max-w-xl text-lg leading-relaxed text-stone-600 dark:text-stone-400">
          Protocols, medications, point-of-care calculators, and continuing education. Works with no signal.
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {isBackendConfigured ? (
            <>
              <ButtonLink to="/login">Sign in</ButtonLink>
              <ButtonLink to="/signup" variant="outline">
                Create account
              </ButtonLink>
            </>
          ) : (
            <ButtonLink to="/tools">Open clinical tools</ButtonLink>
          )}
        </div>
      </section>

      <QuickTools />

      <section>
        <SectionTitle>Reference</SectionTitle>
        <ul className={ruledList}>
          {[
            { to: '/meds', icon: Pill, title: 'Reference formulary', text: 'Monographs and dose rules for common EMS medications' },
            { to: '/library', icon: Library, title: 'State protocol library', text: 'Model protocols by state, before your agency customizes them' },
          ].map((item) => (
            <li key={item.to}>
              <Link to={item.to} className={cn('group flex items-center gap-3.5 px-1 py-3.5', rowHover)}>
                <item.icon className="size-4 shrink-0 text-stone-400" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{item.title}</span>
                  <span className="block text-sm text-stone-500 dark:text-stone-400">{item.text}</span>
                </span>
                <ArrowRight className="size-4 text-stone-300 transition group-hover:translate-x-0.5 group-hover:text-stone-900 dark:text-stone-600 dark:group-hover:text-white" />
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

export function HomePage() {
  const { user, profile, activeMembership } = useAuth();
  const acks = usePendingAcknowledgments();
  const assignments = useMyAssignments();
  const notifications = useNotifications();

  if (!user) return <SignedOutHome />;

  const openAssignments = (assignments.data ?? []).filter((a) => a.status === 'assigned');
  const overdue = openAssignments.filter((a) => a.assignment.due_at && new Date(a.assignment.due_at) < new Date());
  const recent = (notifications.data ?? []).filter((n) => n.kind === 'announcement' || n.kind === 'protocol_update').slice(0, 4);
  const firstName = profile?.full_name?.split(' ')[0];

  return (
    <div className="space-y-10">
      <header className="border-b border-stone-300 pb-5 dark:border-stone-700">
        <p className="label-caps text-stone-500 dark:text-stone-400">
          {formatDate(new Date(), { weekday: 'long', month: 'long', day: 'numeric' })}
          {activeMembership && ` · ${activeMembership.organization.name}`}
        </p>
        <h1 className="mt-2 text-[1.75rem] font-semibold leading-tight tracking-tight">{firstName ? `Welcome back, ${firstName}` : 'Welcome'}</h1>
      </header>

      {!activeMembership && (
        <Alert tone="info" title="You’re not connected to an agency yet" action={<ButtonLink size="sm" variant="outline" to="/profile">Get started</ButtonLink>}>
          Open the invite link from your administrator, or create an organization to publish your own protocols.
        </Alert>
      )}

      {(acks.data?.length ?? 0) > 0 && (
        <section>
          <SectionTitle>Protocol updates to acknowledge</SectionTitle>
          <ul className="divide-y divide-amber-200 border-l-[3px] border-amber-500 bg-amber-50/60 dark:divide-amber-400/15 dark:border-amber-400 dark:bg-amber-400/5">
            {acks.data!.map((a) => (
              <li key={a.protocol_id}>
                <Link to={`/protocols/${a.protocol_id}`} className="group flex items-center gap-3 px-4 py-3 hover:bg-amber-100/60 dark:hover:bg-amber-400/10">
                  <span className="flex-1 font-medium">{a.title}</span>
                  <span className="font-mono text-xs text-amber-800 dark:text-amber-300">v{a.version}</span>
                  <ArrowRight className="size-4 text-amber-700/60 transition group-hover:translate-x-0.5 dark:text-amber-300/60" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <QuickTools />

      <div className="grid gap-10 lg:grid-cols-2">
        <section>
          <SectionTitle action={<Link to="/learn/assignments" className={textLink}>View all</Link>}>
            Assignments{' '}
            <span className={cn('ml-1', overdue.length > 0 && 'text-red-700 dark:text-red-400')}>
              {overdue.length > 0 ? `· ${overdue.length} overdue` : `· ${openAssignments.length} open`}
            </span>
          </SectionTitle>
          {openAssignments.length === 0 ? (
            <p className="border-y border-stone-200 py-6 text-sm text-stone-500 dark:border-stone-800">Nothing assigned right now.</p>
          ) : (
            <ul className={ruledList}>
              {openAssignments.slice(0, 5).map((a) => (
                <li key={a.assignment_id} className="flex items-center gap-3 py-3">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{a.assignment.title}</span>
                    {a.assignment.due_at && <span className="text-sm text-stone-500">Due {formatDate(a.assignment.due_at)}</span>}
                  </span>
                  {a.assignment.due_at && new Date(a.assignment.due_at) < new Date() && <Badge tone="danger">Overdue</Badge>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <SectionTitle action={<Link to="/inbox" className={textLink}>Inbox</Link>}>Latest updates</SectionTitle>
          {recent.length === 0 ? (
            <p className="border-y border-stone-200 py-6 text-sm text-stone-500 dark:border-stone-800">No announcements yet.</p>
          ) : (
            <ul className={ruledList}>
              {recent.map((n) => (
                <li key={n.id}>
                  <Link
                    to={n.kind === 'announcement' && n.announcement_id ? `/inbox/${n.announcement_id}` : (n.url ?? '/inbox')}
                    className={cn('flex items-baseline gap-3 px-1 py-3', rowHover)}
                  >
                    <BookOpen className="size-3.5 shrink-0 translate-y-0.5 text-stone-400" />
                    <span className="min-w-0 flex-1 font-medium">{n.title}</span>
                    <span className="shrink-0 font-mono text-xs text-stone-500">{relativeTime(n.created_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
